import React from "react";
import { safetyRules, goldenBasics } from "../electronicsKitData";
import {
  ShieldAlert,
  AlertTriangle,
  Power,
  Baby,
  CheckCircle2,
  Sparkles,
} from "lucide-react";
import {
  LedPolarityDiagram,
  PushButtonDiagram,
  TwistTapeDiagram,
} from "./CircuitDiagrams";

const getSafetyIcon = (icon) => {
  switch (icon) {
    case "alert-triangle":
      return <AlertTriangle size={24} className="text-amber-500" />;
    case "shield-alert":
      return <ShieldAlert size={24} className="text-red-500" />;
    case "power":
      return <Power size={24} className="text-blue-500" />;
    case "baby":
      return <Baby size={24} className="text-purple-500" />;
    default:
      return <AlertTriangle size={24} className="text-amber-500" />;
  }
};

const SafetyBasicsSection = () => {
  return (
    <section className="section safety-basics-section" id="safety-basics">
      <div className="container">
        {/* Safety Header */}
        <div className="section-header text-center reveal-triggered fade-up" data-aos="fade-up">
          <div className="section-badge safety-badge">
            <ShieldAlert size={16} /> Before You Start
          </div>
          <h2 className="section-title">Stay Safe: 4 Simple Guidelines</h2>
          <p className="section-subtitle">
            Safety comes first in electronics. Keep these four golden rules in mind while building your circuits.
          </p>
        </div>

        {/* 4 Safety Rules Cards */}
        <div className="safety-grid">
          {safetyRules.map((rule, idx) => (
            <div
              key={rule.id}
              className="safety-card reveal-triggered zoom-in"
              data-aos="zoom-in"
              data-aos-delay={(idx + 1) * 100}
            >
              <div className="safety-card-icon-bar">
                <div className="safety-icon-wrapper">{getSafetyIcon(rule.icon)}</div>
                <span className="safety-pill">{rule.badge}</span>
              </div>
              <h3 className="safety-rule-title">{rule.title}</h3>
              <p className="safety-rule-desc">{rule.desc}</p>
            </div>
          ))}
        </div>

        {/* 3 Things to Remember Section */}
        <div className="golden-rules-container mt-16">
          <div className="section-header text-center reveal-triggered fade-up" data-aos="fade-up">
            <div className="section-badge basics-badge">
              <Sparkles size={16} /> Circuit Fundamentals
            </div>
            <h2 className="section-title">3 Things to Remember</h2>
            <p className="section-subtitle">
              Memorize these 3 basic rules and every single project will work on the first try!
            </p>
          </div>

          <div className="golden-rules-grid">
            {/* Rule 1: LED Polarity */}
            <div className="rule-card reveal-triggered fade-up" data-aos="fade-up" data-aos-delay="100">
              <div className="rule-card-header">
                <div className="rule-number-badge">🦒 Rule 1</div>
                <h3>The Giraffe Leg (LED Polarity)</h3>
              </div>
              <p className="rule-summary">Long leg loves Red (+), Short leg loves Black (−)! Wrong way round = no light, just flip it round.</p>
              <div className="rule-diagram-wrapper">
                <img
                  src="/img/electronics-kit/rule_1_led_legs.png"
                  alt="LED Legs: Long leg is red plus, short leg is black minus"
                  className="w-full h-auto rounded-xl object-contain shadow-sm"
                  loading="lazy"
                />
              </div>
              <div className="rule-tip-box">
                <CheckCircle2 size={16} className="text-emerald-500 shrink-0" />
                <span>Wrong way round = no light. If your LED does not glow, simply turn it round!</span>
              </div>
            </div>

            {/* Rule 2: Push Button Opposite Corners */}
            <div className="rule-card reveal-triggered fade-up" data-aos="fade-up" data-aos-delay="200">
              <div className="rule-card-header">
                <div className="rule-number-badge">💃 Rule 2</div>
                <h3>The Diagonal Dance (Push Button)</h3>
              </div>
              <p className="rule-summary">Always connect wires across opposite diagonal corners for a reliable click every time.</p>
              <div className="rule-diagram-wrapper">
                <img
                  src="/img/electronics-kit/rule_2_push_button.png"
                  alt="Push button: Opposite corners = works every time"
                  className="w-full h-auto rounded-xl object-contain shadow-sm"
                  loading="lazy"
                />
              </div>
              <div className="rule-tip-box">
                <CheckCircle2 size={16} className="text-emerald-500 shrink-0" />
                <span>Opposite diagonal corners work every single time you press the button.</span>
              </div>
            </div>

            {/* Rule 3: Twist & Tape Joints */}
            <div className="rule-card reveal-triggered fade-up" data-aos="fade-up" data-aos-delay="300">
              <div className="rule-card-header">
                <div className="rule-number-badge">🐻 Rule 3</div>
                <h3>The Metal Bear Hug (Twist & Tape)</h3>
              </div>
              <p className="rule-summary">Metal must touch bare metal with a tight 3-4 twist handshake! Tape holds the hug firmly together.</p>
              <div className="rule-diagram-wrapper">
                <img
                  src="/img/electronics-kit/rule_3_how_to_join.png"
                  alt="How to join: 1. Cross ends, 2. Twist 3-4 times, 3. Tape tightly"
                  className="w-full h-auto rounded-xl object-contain shadow-sm"
                  loading="lazy"
                />
              </div>
              <div className="rule-tip-box">
                <CheckCircle2 size={16} className="text-emerald-500 shrink-0" />
                <span>Metal must touch bare metal! Tape only holds the joint together firmly.</span>
              </div>
            </div>
          </div>

          {/* Golden Rule Summary Banner */}
          <div className="golden-rule-banner reveal-triggered zoom-in" data-aos="zoom-in" data-aos-delay="200">
            <div className="banner-icon">⚡</div>
            <div className="banner-text">
              <h4>Golden Assembly Rule:</h4>
              <p>Always clip the battery on LAST, after every wire joint is twisted and taped tightly!</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SafetyBasicsSection;
